import { DataTableColumnType } from '../../common/_custom';
import { DataTableColumn } from '../../common/_custom/DataTable/DataTable.types';
import { AggregatedDealTerms, DebtOptionTagType, Metric } from './debtProductTypes';

/** Aria-label / title for row selection checkbox (product vs lender). */
export const ROW_SELECT_LABEL = {
    SELECT_PRODUCT: 'Select product',
    SELECT_LENDER: 'Select lender',
} as const;

/** Constants for debt option tag values (product/lender tags). Use with DebtOptionTagType. */
export const DEBT_OPTION_TAG = {
    REQUESTED: 'Requested',
    PREFERRED: 'Preferred',
    EXCLUDED: 'Excluded',
} as const;

/** Display labels for debt option tags (API value may differ from UI label, e.g. Preferred → Top Pick). */
export const DEBT_OPTION_TAG_DISPLAY_LABEL = {
    PREFERRED: 'Top Pick',
} as const;

/** Config for debt option tag pills (tooltip + label). ClassName is applied in the component from CSS module. */
export const DEBT_OPTION_TAG_CONFIG: Record<DebtOptionTagType, { tooltip: string | null; label: string }> = {
    [DEBT_OPTION_TAG.REQUESTED]: {
        tooltip: 'Borrower preferred debt product',
        label: DEBT_OPTION_TAG.REQUESTED,
    },
    [DEBT_OPTION_TAG.PREFERRED]: {
        tooltip: 'Preferred lender shows top engagement and quick responses.',
        label: DEBT_OPTION_TAG_DISPLAY_LABEL.PREFERRED,
    },
    [DEBT_OPTION_TAG.EXCLUDED]: {
        tooltip: 'This lender has been marked as excluded by the company',
        label: DEBT_OPTION_TAG.EXCLUDED,
    },
};

/** Map API tag string (e.g. "REQUESTED") to DebtOptionTagType for normalizing tag keys. */
export const DEBT_OPTION_TAG_NORMALIZE_MAP: Record<string, DebtOptionTagType> = {
    REQUESTED: DEBT_OPTION_TAG.REQUESTED,
    PREFERRED: DEBT_OPTION_TAG.PREFERRED,
    EXCLUDED: DEBT_OPTION_TAG.EXCLUDED,
};

export const AUDITED_FINANCIAL_KEY_METRIC: Metric[] = [
    'Revenue',
    'RevenueGrowthPercentage',
    'EbitaMargin',
    'PAT',
    'CashAndCashEquivalent',
    'TotalCurrentAssets',
    'TotalCurrentLiabilities',
    'NetWorkingCapital',
    'LongTermDebt',
    'ShortTermDebt',
    'TangibleNetWorth',
    'TotalCosts',
    'TradePayables',
    'TradeReceivables',
    'Inventories',
    'AnnualDebtRepayments',
];

export type MandateTermConfig = {
    key: string;
    label: string;
    unit?: string;
};

/**
 * Configuration for editable number fields in debt products table
 */
export type EditableFieldConfig = {
    field: 'amount' | 'tenure' | 'roi';
    headerLabel: string;
    unit: string;
    unitLeft?: boolean;
    secondaryUnit?: string;
    allowDecimal?: boolean; // If false, prevents decimal input (default: true)
};

export const editableFieldsConfig: EditableFieldConfig[] = [
    { field: 'amount', headerLabel: 'Amount', unit: '₹', unitLeft: true, secondaryUnit: 'Cr' },
    { field: 'tenure', headerLabel: 'Tenure', unit: 'm', allowDecimal: false },
    { field: 'roi', headerLabel: 'ROI', unit: '%' },
];

/**
 * Get editable field config by field name
 */
export const getEditableFieldConfig = (field: string): EditableFieldConfig | undefined => {
    return editableFieldsConfig.find((config) => config.field === field);
};

export const mandateTermsMapping: MandateTermConfig[] = [
    { key: 'maximumDealAmount', label: 'Amount', unit: 'Cr' },
    { key: 'roi', label: 'ROI', unit: '%' },
    { key: 'tenure', label: 'Tenure', unit: 'years' },
    { key: 'products', label: 'Products' },
    { key: 'securityOffered', label: 'Security' },
    { key: 'EndUseofCapital', label: 'End Use' },
];

export const defaultAggregatedDealTerms: AggregatedDealTerms = {
    maximumDealAmount: 0,
    minimumRoi: 0,
    maximumRoi: 0,
    minimumTenure: 0,
    maximumTenure: 0,
    products: [],
    securityOffered: [],
    EndUseofCapital: '',
};

export const mandateSharingColumns: DataTableColumn[] = [
    {
        field: 'lenderName',
        headerLabel: 'Lenders Selected',
        headerOriginalLabel: 'Lenders Selected',
        type: DataTableColumnType.TEXT,
        width: '20%',
    },
    {
        field: 'amount',
        headerLabel: 'Amount',
        headerOriginalLabel: 'Amount',
        type: DataTableColumnType.TEXT,
        width: '10%',
    },
    {
        field: 'tenure',
        headerLabel: 'Tenure',
        headerOriginalLabel: 'Tenure',
        type: DataTableColumnType.TEXT,
        width: '10%',
    },
    {
        field: 'roi',
        headerLabel: 'ROI',
        headerOriginalLabel: 'ROI',
        type: DataTableColumnType.TEXT,
        width: '10%',
    },
    {
        field: 'loanTypes',
        headerLabel: 'Products',
        headerOriginalLabel: 'Products',
        type: DataTableColumnType.PILL,
        width: '20%',
    },
    {
        field: 'security',
        headerLabel: 'Security',
        headerOriginalLabel: 'Security',
        type: DataTableColumnType.PILL,
        width: '20%',
    },
    {
        field: 'lenderUsers',
        headerLabel: 'Share Deal With',
        headerOriginalLabel: 'Share Deal With',
        type: DataTableColumnType.COMPONENT,
        width: '22%',
    },
    {
        field: 'isShared',
        headerLabel: 'Status',
        headerOriginalLabel: 'Status',
        type: DataTableColumnType.PILL,
        width: '10%',
    },
];

export enum DealPayloadScenario {
    CREATE = 'CREATE',
    UPDATE_BASE = 'UPDATE_BASE',
    UPDATE_NOTES_DOCS = 'UPDATE_NOTES_DOCS',
    UPDATE_AGGREGATED_TERMS = 'UPDATE_AGGREGATED_TERMS',
}

export enum ButtonText {
    VIEW_MANDATE = 'View Mandate',
    VIEW_DEALS = 'View Deals',
    SHARE_MANDATE = 'Share Mandate',
    UPDATE_MANDATE = 'Update Mandate',
    CREATE_MANDATE = 'Create Mandate',
    DISCARD_CHANGES = 'Discard Changes',
    PROCEED = 'Proceed',
    CLOSE = 'Close',
}

export const SHARED_DEAL = 'SHARED_DEAL';

export enum EntityType {
    PRODUCT = 'product',
    LENDER = 'lender',
}

export const DEAL_SHARED_FOR_ANOTHER_PRODUCT = 'Deal has been shared for another product.';

export const RECOMMENDED_VALUE_TOOLTIP_CONTENT = 'Recommended Value';

export const SECURITY_FORM_KEY = 'securityOffered';

export const PRODUCT_TERMS_KEYS = {
    ROI: 'roi',
    TENURE: 'tenure',
    AMOUNT: 'amount',
};

export const PROBABILITY_TOOLTIP_MESSAGES = {
    LOW_SCORE: 'A low score is less likely to attract lender interest. \nClick for more details. ',
    HIGH_SCORE: 'A high score is more likely to attract lender interest. \nClick for more details. ',
};

export const NAVIGATION_CONSTANTS = {
    COMPANY_DEALS_WITH_DEAL_UPDATED: (assesseeOrgId: string) =>
        `/investor/app/company-deals/${assesseeOrgId}?page=1&fetchType=all&type=deals&category=DEAL_VIEW&tab=deals&dealUpdated=true`,
    DEALS_WITH_DEAL_UPDATED: '/investor/app/deals?page=1&type=deals&dealUpdated=true',
    COMPANY_DEALS_WITH_MANDATE_UPDATED: (assesseeOrgId?: string) =>
        `/investor/app/company-deals/${assesseeOrgId}?page=1&fetchType=all&type=deals&category=MANDATE_VIEW&tab=deals`,
};

export const ENTITY_TYPE_KEYS = {
    PRODUCT: 'productName',
    LENDER: 'lenderName',
    SELECT: 'select',
    CARET: 'caret',
    PROBABILITY: 'probability',
    SECURITY: 'security',
    AMOUNT: 'amount',
    TENURE: 'tenure',
    ROI: 'roi',
    LATEST_DEAL_SHARED: 'latestSharedDealDate',
    TIMELINE: 'timeline',
};

export const DATA_SOURCE_FOR_LAST_UPDATED_DATA_TYPE = {
    ACTUAL_DATA: 'actual data',
    DISCUSSION_DATA: 'discussion data',
    AUDITED_DATA: 'audited data',
};

export const originalKeyMap: Record<string, string> = {
    amount: 'originalAmount',
    tenure: 'originalTenure',
    roi: 'originalRoi',
};

export const metricSourceMap: Record<string, string> = {
    amount: 'LIMIT_METRIC',
    tenure: 'TENURE_METRIC',
    roi: 'ROI_METRIC',
    security: 'SECURITY_METRIC',
};

export const ITALIC_TEXT_METRIC_SOURCES = ['BRE', 'PEC'];
